import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Play, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';

interface BalloonPopperProps {
  onBack: () => void;
}

interface Balloon {
  id: number;
  x: number;
  y: number;
  color: string;
  speed: number;
}

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'];
const DWELL_TIME = 1000; // 1 second to pop

const BalloonPopper: React.FC<BalloonPopperProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [balloons, setBalloons] = useState<Balloon[]>([]);
  const [score, setScore] = useState(0);
  const [missedCount, setMissedCount] = useState(0);
  const [hoveredBalloon, setHoveredBalloon] = useState<number | null>(null);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  const gameLoopRef = useRef<number>();
  const balloonIdRef = useRef(0);
  const dwellStartRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const GAME_WIDTH = 800;
  const GAME_HEIGHT = 500;
  const BALLOON_WIDTH = 60;
  const BALLOON_HEIGHT = 80;
  const MAX_MISSED = 5;

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setMissedCount(0);
    setBalloons([]);
    balloonIdRef.current = 0;
  };

  const popBalloon = useCallback((id: number) => {
    setBalloons(prev => prev.filter(b => b.id !== id));
    setScore(prev => prev + 10);
    setHoveredBalloon(null);
    setDwellProgress(0);
    dwellStartRef.current = null;
  }, []);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Dwell detection
  useEffect(() => {
    if (!isPlaying || gameOver || hoveredBalloon === null) {
      dwellStartRef.current = null;
      setDwellProgress(0);
      return;
    }

    const checkDwell = () => {
      if (dwellStartRef.current === null) {
        dwellStartRef.current = Date.now();
      }

      const elapsed = Date.now() - dwellStartRef.current;
      const progress = Math.min(elapsed / DWELL_TIME, 1);
      setDwellProgress(progress);

      if (progress >= 1) {
        popBalloon(hoveredBalloon);
      }
    };

    const interval = setInterval(checkDwell, 50);
    return () => clearInterval(interval);
  }, [hoveredBalloon, isPlaying, gameOver, popBalloon]);

  // Game loop - move balloons
  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const gameLoop = () => {
      setBalloons(prev => {
        const updated = prev.map(b => ({ ...b, y: b.y - b.speed }));
        
        // Check for missed balloons
        const missed = updated.filter(b => b.y < -BALLOON_HEIGHT);
        if (missed.length > 0) {
          setMissedCount(prev => {
            const newCount = prev + missed.length;
            if (newCount >= MAX_MISSED) {
              setGameOver(true);
              setIsPlaying(false);
            }
            return newCount;
          });
        }

        return updated.filter(b => b.y >= -BALLOON_HEIGHT);
      });

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [isPlaying, gameOver]);

  // Spawn balloons
  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const spawnInterval = setInterval(() => {
      setBalloons(prev => [
        ...prev,
        {
          id: balloonIdRef.current++,
          x: Math.random() * (GAME_WIDTH - BALLOON_WIDTH),
          y: GAME_HEIGHT + BALLOON_HEIGHT,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          speed: 1 + Math.random() * 1.5,
        }
      ]);
    }, 1200);

    return () => clearInterval(spawnInterval);
  }, [isPlaying, gameOver]);

  // Check balloon hover
  useEffect(() => {
    if (!isPlaying || gameOver) return;

    const hoveredId = balloons.find(balloon => {
      const centerX = balloon.x + BALLOON_WIDTH / 2;
      const centerY = balloon.y + BALLOON_HEIGHT / 2;
      const distance = Math.sqrt(
        Math.pow(mousePos.x - centerX, 2) + Math.pow(mousePos.y - centerY, 2)
      );
      return distance < BALLOON_WIDTH / 2;
    })?.id ?? null;

    if (hoveredId !== hoveredBalloon) {
      setHoveredBalloon(hoveredId);
      dwellStartRef.current = null;
      setDwellProgress(0);
    }
  }, [mousePos, balloons, isPlaying, gameOver, hoveredBalloon]);

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-sky-400 to-sky-200 flex flex-col overflow-hidden z-[100]" dir="ltr">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-800/50">
        <Button
          onClick={onBack}
          variant="outline"
          size="lg"
          className="bg-slate-700 border-cyan-400 text-cyan-400 hover:bg-cyan-400/20"
        >
          <ArrowLeft className="mr-2 h-6 w-6" />
          {t("back")}
        </Button>
        <div className="flex gap-8">
          <div className="text-3xl font-bold text-yellow-400">
            {t("score")}: {score}
          </div>
          <div className="text-3xl font-bold text-red-400">
            {t("missed")}: {missedCount}/{MAX_MISSED}
          </div>
        </div>
      </div>

      {/* Game Area */}
      <div className="flex-1 flex items-center justify-center">
        <div 
          ref={containerRef}
          className="relative bg-gradient-to-b from-sky-300 to-sky-100 border-4 border-white rounded-lg overflow-hidden shadow-2xl"
          style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}
        >
          {/* Clouds decoration */}
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-70"
              style={{
                width: 80 + Math.random() * 60,
                height: 40 + Math.random() * 20,
                left: Math.random() * GAME_WIDTH,
                top: Math.random() * 100,
              }}
            />
          ))}

          {/* Balloons */}
          <AnimatePresence>
            {balloons.map(balloon => (
              <motion.div
                key={balloon.id}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                className="absolute cursor-pointer"
                style={{
                  left: balloon.x,
                  top: balloon.y,
                  width: BALLOON_WIDTH,
                  height: BALLOON_HEIGHT,
                }}
              >
                {/* Balloon body */}
                <div
                  className="w-full rounded-full transition-transform"
                  style={{
                    height: BALLOON_WIDTH,
                    backgroundColor: balloon.color,
                    boxShadow: hoveredBalloon === balloon.id 
                      ? `0 0 30px ${balloon.color}, 0 0 60px ${balloon.color}` 
                      : `0 4px 15px ${balloon.color}50`,
                    transform: hoveredBalloon === balloon.id ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  {/* Highlight */}
                  <div 
                    className="absolute top-2 left-2 w-4 h-4 bg-white rounded-full opacity-50"
                  />
                </div>
                {/* String */}
                <div 
                  className="absolute left-1/2 -translate-x-1/2 w-0.5 bg-slate-400"
                  style={{ height: 20, top: BALLOON_WIDTH }}
                />

                {/* Dwell progress ring */}
                {hoveredBalloon === balloon.id && dwellProgress > 0 && (
                  <svg
                    className="absolute -inset-2"
                    style={{ width: BALLOON_WIDTH + 16, height: BALLOON_WIDTH + 16 }}
                  >
                    <circle
                      cx={(BALLOON_WIDTH + 16) / 2}
                      cy={(BALLOON_WIDTH + 16) / 2}
                      r={(BALLOON_WIDTH + 8) / 2}
                      fill="none"
                      stroke="white"
                      strokeWidth="4"
                      strokeDasharray={Math.PI * (BALLOON_WIDTH + 8)}
                      strokeDashoffset={Math.PI * (BALLOON_WIDTH + 8) * (1 - dwellProgress)}
                      strokeLinecap="round"
                      style={{
                        filter: 'drop-shadow(0 0 5px white)',
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center',
                      }}
                    />
                  </svg>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Custom cursor with dwell indicator */}
          <div
            className="pointer-events-none absolute z-50"
            style={{
              left: mousePos.x - 20,
              top: mousePos.y - 20,
              width: 40,
              height: 40,
            }}
          >
            <svg width="40" height="40">
              {/* Background circle */}
              <circle
                cx="20"
                cy="20"
                r="18"
                fill="none"
                stroke="rgba(0,0,0,0.3)"
                strokeWidth="3"
              />
              {/* Progress circle */}
              {dwellProgress > 0 && (
                <circle
                  cx="20"
                  cy="20"
                  r="18"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="4"
                  strokeDasharray={Math.PI * 36}
                  strokeDashoffset={Math.PI * 36 * (1 - dwellProgress)}
                  strokeLinecap="round"
                  style={{
                    transform: 'rotate(-90deg)',
                    transformOrigin: 'center',
                    filter: 'drop-shadow(0 0 3px #22c55e)',
                  }}
                />
              )}
              {/* Center dot */}
              <circle
                cx="20"
                cy="20"
                r="4"
                fill={dwellProgress > 0.5 ? '#22c55e' : 'white'}
              />
            </svg>
          </div>

          {/* Start/Game Over overlay */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
              <div className="text-center">
                {gameOver ? (
                  <>
                    <h2 className="text-5xl font-bold text-red-400 mb-4">{t("gameOver")}</h2>
                    <p className="text-3xl text-yellow-400 mb-8">{t("score")}: {score}</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-5xl font-bold text-cyan-400 mb-4">{t("balloonPopper")}</h2>
                    <p className="text-xl text-slate-300 mb-8">
                      {t("balloonPopperInstructions")}
                    </p>
                  </>
                )}
                <Button
                  onClick={startGame}
                  size="lg"
                  className="bg-yellow-400 text-slate-900 hover:bg-yellow-300 text-2xl px-8 py-6"
                >
                  {gameOver ? <RotateCcw className="mr-2 h-8 w-8" /> : <Play className="mr-2 h-8 w-8" />}
                  {gameOver ? t("playAgain") : t("startGame")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="p-4 text-center text-slate-700 text-lg font-medium">
        {t("balloonPopperHint")}
      </div>
    </div>
  );
};

export default BalloonPopper;
